/**
 * 世界系统类型定义
 * 包含游戏时间、地理位置、天气等
 */

import { z } from 'zod';
import { RankSchema } from '@/IsekaiRPG/shared/types';
import { CharacterSeedDefinitionSchema } from '@/IsekaiRPG/modules/character/characterTypes';
import { CityStateSchema } from '@/IsekaiRPG/modules/city/cityTypes';
import { WorldSiteRoomBlueprintSlotSchema as SharedWorldSiteRoomBlueprintSlotSchema } from '@/IsekaiRPG/modules/exploration/types/explorationShared';
import { RoomTemplateSchema } from '@/IsekaiRPG/modules/exploration/types/roomTemplate';
import { SiteContentPlanSchema } from '@/IsekaiRPG/modules/exploration/types/siteContentPlan';
import { ItemBaseTemplateSchema } from '@/IsekaiRPG/modules/item/itemTypes';
import { SemanticQuestSiteGenerationStatusSchema } from '@/IsekaiRPG/modules/quest/questTypes';
import { TagDefinitionSchema } from '@/IsekaiRPG/modules/tags/tagTypes';

// ============================================
// 枚举定义
// ============================================

export const WeatherTypeSchema = z.enum([
  'sunny', // 晴朗
  'cloudy', // 多云
  'rainy', // 雨
  'stormy', // 暴风雨
  'snowy', // 雪
  'foggy', // 雾
  'mystic', // 神秘/魔力风暴
]);

export type WeatherType = z.infer<typeof WeatherTypeSchema>;

export const SeasonSchema = z.enum(['spring', 'summer', 'autumn', 'winter']);

export type Season = z.infer<typeof SeasonSchema>;

// ============================================
// 世界状态组件
// ============================================

/**
 * 游戏内时间
 * 月直接作为独立字段持久化，避免 UI / 服务层根据 day 反推
 */
export const GameTimeSchema = z.object({
  year: z.number().int().min(1).default(1223),
  season: SeasonSchema.default('spring'),
  month: z.number().int().min(1).max(12).default(1),
  day: z.number().int().min(1).default(1),
  hour: z.number().int().min(0).max(23).default(10),
  minute: z.number().int().min(0).max(59).default(0),
});

export type GameTime = z.infer<typeof GameTimeSchema>;

/**
 * 世界环境参数 (Isekai 特色)
 */
export const WorldEnvironmentSchema = z.object({
  manaDensity: z.number().min(0).default(1.0), // 魔力浓度，影响法术效果
});

export type WorldEnvironment = z.infer<typeof WorldEnvironmentSchema>;

// ============================================
// 世界地点 (World Site) / 兴趣点 (POI)
// ============================================

/**
 * 地点类型枚举
 */
export const WorldSiteTypeSchema = z.enum([
  'dungeon', // 地牢/副本 (可进入)
  'ruins', // 废墟/遗迹 (探索)
  'resource', // 资源点 (采集)
  'monster_camp', // 怪物营地 (战斗)
  'event', // 特殊事件点 (剧情)
]);

export type WorldSiteType = z.infer<typeof WorldSiteTypeSchema>;

export const WorldSiteRoomBlueprintSlotSchema = SharedWorldSiteRoomBlueprintSlotSchema;
export type WorldSiteRoomBlueprintSlot = z.infer<typeof WorldSiteRoomBlueprintSlotSchema>;

const OptionalNonEmptyStringSchema = z.preprocess(value => {
  if (value == null) return undefined;
  if (typeof value !== 'string') return value;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
}, z.string().min(1).optional());

export const WorldSiteRoomBlueprintSchema = z.object({
  slot: WorldSiteRoomBlueprintSlotSchema,
  templateId: OptionalNonEmptyStringSchema,
  title: z.string(),
  description: z.string(),
  tags: z.array(z.string()).default([]),
  isUniqueRunContent: z.boolean().default(false),
});
export type WorldSiteRoomBlueprint = z.infer<typeof WorldSiteRoomBlueprintSchema>;

export const WorldSiteRoomBlueprintsSchema = z.object({
  rooms: z.array(WorldSiteRoomBlueprintSchema).default([]),
});
export type WorldSiteRoomBlueprints = z.infer<typeof WorldSiteRoomBlueprintsSchema>;

export const AIWorldSiteSchema = z.object({
  name: z.string(),
  type: z.enum(['ruins', 'cave', 'forest', 'tower', 'dungeon', 'village']),
  description: z.string(),
  difficultyRank: RankSchema,
  theme: z.string().optional(),
  tags: z.array(z.string()).default([]),
});
export type AIWorldSite = z.infer<typeof AIWorldSiteSchema>;

/**
 * 世界地点/站点 Schema
 * 用于描述地图上动态生成的兴趣点
 */
export const WorldSiteSchema = z.object({
  id: z.string(), // 唯一ID (UUID)
  x: z.number(), // 世界坐标 X
  y: z.number(), // 世界坐标 Y
  type: WorldSiteTypeSchema, // 站点类型
  name: z.string(), // 显示名称
  description: z.string().optional(), // 描述
  theme: z.string().optional(), // 据点整体主题描述
  tags: z.array(z.string()).default([]), // 据点整体标签
  difficulty: z.number().default(1), // 难度等级
  status: z.enum(['undiscovered', 'discovered', 'cleared']).default('discovered'), // 状态
  retention: z.enum(['temporary', 'favorited']).default('temporary'), // 保留策略：临时/收藏

  // 机制
  roomBlueprints: WorldSiteRoomBlueprintsSchema.optional(), // AI 预生成的探索房间蓝图

  // 元数据
  createdAt: z.number().optional(), // 生成时间
  lastVisitedAt: z.number().optional(), // 最近访问时间
});

export type WorldSite = z.infer<typeof WorldSiteSchema>;

export const SemanticQuestSiteRuntimeEntrySchema = z.object({
  status: SemanticQuestSiteGenerationStatusSchema.default('idle'),
  lastError: z.string().optional(),
});
export type SemanticQuestSiteRuntimeEntry = z.infer<typeof SemanticQuestSiteRuntimeEntrySchema>;

// ============================================
// 完整世界状态
// ============================================

/**
 * 世界状态
 */
export const WorldStateSchema = z.object({
  unlockedAreas: z.array(z.string()).default([]),

  // 动态生成的地点列表 (POI)
  sites: z.array(WorldSiteSchema).default([]),

  // 统一的据点下游内容计划池（任务据点 / 自由探索据点共用）
  siteContentPlans: z.record(z.string(), SiteContentPlanSchema).default({}),

  // 任务据点运行态池：仅保存非任务语义本体的据点生成过程状态
  semanticQuestSiteRuntime: z.record(z.string(), SemanticQuestSiteRuntimeEntrySchema).default({}),

  // 统一的非内建物品模板持久化池
  persistedItemTemplates: z.record(z.string(), ItemBaseTemplateSchema).default({}),

  // 统一的非内建房间模板持久化池
  persistedRoomTemplates: z.record(z.string(), RoomTemplateSchema).default({}),

  // 统一的非内建人物种子持久化池
  persistedCharacterSeedDefinitions: z.record(z.string(), CharacterSeedDefinitionSchema).default({}),

  // 统一的动态标签持久化池（候选/晋升标签）
  persistedTagDefinitions: z.record(z.string(), TagDefinitionSchema).default({}),

  /**
   * 城市状态详情
   * Key: 城市 ID (Burg ID)
   * Value: 城市内的状态数据 (建筑分布等)
   */
  cityStates: z.record(z.string(), CityStateSchema).default({}),

  // 事件标记 - 使用联合类型提供更明确的值类型
  flags: z.record(z.string(), z.union([z.boolean(), z.number(), z.string()])).default({}),

  // 游戏内时间
  time: GameTimeSchema.default({
    year: 1223,
    season: 'spring',
    month: 1,
    day: 1,
    hour: 10,
    minute: 0,
  }),

  // 天气
  weather: WeatherTypeSchema.default('sunny'),

  // 环境状态
  environment: WorldEnvironmentSchema.default({
    manaDensity: 1.0,
  }),
});

export type WorldState = z.infer<typeof WorldStateSchema>;
